import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { IProduct } from '@models/product';
import { IReaggregationHistoryItem } from '@models/Reaggregation/reaggregation-history-item';
import { IReaggregationStatusItem } from '@models/Reaggregation/reaggregation-status-item';
import { IReaggregationStatusIndicatorType, REAGGREGATION_STATUS } from '@shared/reaggregation-status';
import { BehaviorSubject, merge, Observable, Subject, timer } from 'rxjs';
import { concatMap, publish, refCount, takeUntil, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ProductReaggregationService implements OnDestroy {
  public readonly destroyed$ = new Subject();

  public PRODUCT_REAGGREGATION_ENDPOINT: string;
  public productReaggregationIndicatorType: IReaggregationStatusIndicatorType = { status: null, hasError: null };
  public reaggregationStatus$ = new BehaviorSubject<IReaggregationStatusItem[]>([]);
  public readonly stopProductReaggregationPolling$ = new Subject();
  public unfinishedReaggregationList = [REAGGREGATION_STATUS.required, REAGGREGATION_STATUS.inProgress];

  constructor(private readonly http: HttpClient, private readonly appConfig: AppConfigProvider) {
    this.appConfig.environment.subscribe(env => {
      this.PRODUCT_REAGGREGATION_ENDPOINT = `${env.API.REFERENCE_DATA}/api/products/reaggregation`;
    });
  }

  ngOnDestroy(): void {
    this.stopProductReaggregationPolling$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public async fetchIdsToBeReaggregated(): Promise<number[]> {
    const request$ = this.http.get(`${this.PRODUCT_REAGGREGATION_ENDPOINT}/check`);
    return (
      request$
        // .pipe(debounceTime(500))
        .toPromise()
        .then((idsToBeReagg: number[]) => {
          // console.log('Setting Reaggregation Status Required for', idsToBeReagg);
          this.setReaggregationStatus(REAGGREGATION_STATUS.required, false, idsToBeReagg);
          return idsToBeReagg;
        })
        .catch(err => {
          console.warn('Error in fetchIdsToBeReaggregated API call');
          // this.handleError(err);
          return null;
        })
    );
  }

  public setReaggregationStatus(status: REAGGREGATION_STATUS, hasError: boolean, ids: number[]) {
    const updatedList = ids.map(id => {
      const origItem = this.reaggregationStatus$.getValue().find(item => item.ProductID === id);
      return {
        ...origItem,
        reaggregationStatus: status,
        HasError: hasError
      };
    });

    this.setReaggregationStatusRef(updatedList);
  }

  public getProductReaggregationStatusPolling(pollingInterval: number, continueConditionFn?): Observable<IReaggregationStatusItem[]> {
    const reaggPolling$ = timer(0, pollingInterval).pipe(
      takeUntil(merge(this.destroyed$, this.stopProductReaggregationPolling$)),

      concatMap(() => this.http.get(`${this.PRODUCT_REAGGREGATION_ENDPOINT}/status`)),
      // SIDE ACTIONS
      tap((reagStatusItems: IReaggregationStatusItem[]) => {
        // console.log('Got latest reaggregation status items:', JSON.parse(JSON.stringify(reagStatusItems)));
        this.setReaggregationStatusRef(reagStatusItems);
        // Calculate the highest grouped Reagg.Status to know If we should display a reagg message or not in product setup page
        this.setProductReaggregationIndicatorType();
      }),
      // STOP CONDITION
      tap(() => {
        if (!continueConditionFn) {
          continueConditionFn = this.checkIfIsSomeUnfinishedReaggregation;
        }
        if (!continueConditionFn()) {
          this.stopProductReaggregationPolling$.next();
        }
      }),
      // Sets same value for every subscription and only one execution of the tap callbacks, If there is multiple subscriptions
      publish(),
      refCount()
    );

    return reaggPolling$;
  }

  public setReaggregationStatusRef(reagStatusItems: IReaggregationStatusItem[]) {
    const hasChanged = JSON.stringify(reagStatusItems) !== JSON.stringify(this.reaggregationStatus$.getValue());
    // Emits only if has changed
    if (hasChanged) {
      this.reaggregationStatus$.next(reagStatusItems);
    }
  }

  public get idsToBeReaggregated(): number[] {
    const idsFlagged = [
      ...new Set(
        this.reaggregationStatus$
          .getValue()
          .filter(({ ReaggregationStatus: reaggregationStatus }) => reaggregationStatus === REAGGREGATION_STATUS.required)
          .map(({ ProductID: productId }) => productId)
      )
    ];
    return idsFlagged;
  }

  public initProductReaggregation(): Promise<void> {
    // Trigger Reaggregation of those Ids
    const request$ = this.http.get(`${this.PRODUCT_REAGGREGATION_ENDPOINT}/start`).pipe(
      tap(() => {
        // Set his reaggregationStatus to "In Progress" once the request has been initializated
        this.setReaggregationStatus(REAGGREGATION_STATUS.inProgress, false, this.idsToBeReaggregated);
      })
    );
    return request$
      .toPromise()
      .then((reaggProds: IProduct[]) => {
        if (reaggProds && reaggProds.hasOwnProperty('length') && reaggProds.length > 0) {
          const succeedIds = reaggProds.map(({ ProductId }) => ProductId);
          const failedIds = this.idsToBeReaggregated.filter(id => !succeedIds.includes(id));
          // Set his reaggregationStatus to "Finished" successfully or with errors
          this.setReaggregationStatus(REAGGREGATION_STATUS.completed, false, succeedIds);
          this.setReaggregationStatus(REAGGREGATION_STATUS.completed, true, failedIds);
        }
      })
      .catch(err => {
        const ids = this.idsToBeReaggregated;
        this.setReaggregationStatus(REAGGREGATION_STATUS.required, false, ids);
        console.warn('Error in initProductReaggregation API call', ids, err);
        // this.handleError(err);
      });
  }

  public setProductReaggregationIndicatorType(): IReaggregationStatusIndicatorType {
    const reaggStatusItemList = this.reaggregationStatus$.getValue();

    if (reaggStatusItemList.length === 0) {
      this.productReaggregationIndicatorType = { status: null, hasError: null };
      return;
    }

    const someInProgress = reaggStatusItemList.some(({ ReaggregationStatus }) => ReaggregationStatus === REAGGREGATION_STATUS.inProgress);
    const someRequiredButAnyInProgress =
      !someInProgress && reaggStatusItemList.some(({ ReaggregationStatus }) => ReaggregationStatus === REAGGREGATION_STATUS.required);
    const allCompleted =
      !someRequiredButAnyInProgress &&
      !reaggStatusItemList.some(({ ReaggregationStatus }) => ReaggregationStatus !== REAGGREGATION_STATUS.completed);
    const someError = reaggStatusItemList.some(({ HasError }) => HasError);

    const status = allCompleted
      ? REAGGREGATION_STATUS.completed
      : someInProgress
      ? REAGGREGATION_STATUS.inProgress
      : someRequiredButAnyInProgress
      ? REAGGREGATION_STATUS.required
      : null;
    this.productReaggregationIndicatorType = {
      status,
      hasError: someError
    };
  }

  public checkIfIsSomeUnfinishedReaggregation = (): boolean =>
    this.unfinishedReaggregationList.includes(this.productReaggregationIndicatorType.status);

  public resetReaggregationStatus() {
    this.setReaggregationStatusRef([]);
  }

  public autoReaggregateIfRequired(): boolean {
    // If there is some item flagged for reaggregation, auto-start reaggregation
    const someFlaggedForReagg = this.reaggregationStatus$.getValue().some(item => item.ReaggregationStatus === -1);
    if (someFlaggedForReagg) {
      this.initProductReaggregation();
    }

    return someFlaggedForReagg;
  }

  public getProductReaggregationHistory(productId: number): Observable<IReaggregationHistoryItem> {
    return this.http.get<IReaggregationHistoryItem>(`${this.PRODUCT_REAGGREGATION_ENDPOINT}/history/${productId}`);
  }
}
